import type { IconName } from '@/lib/icons';
import { Permission } from '@brandschutz/shared';

/**
 * Datenquelle der Hauptnavigation — Label, Icon, Berechtigung UND die
 * Kurzbeschreibung für den Hover-/Fokus-Tooltip liegen bewusst an einer
 * Stelle. Verstreute Text-Literale in der JSX laufen sonst auseinander.
 *
 * Eigene Datei statt in Sidebar.tsx: so ist die Liste ohne Renderer testbar
 * (nav-items.spec.ts wacht über eindeutige Icons) und die Komponentendatei
 * exportiert weiterhin nur Komponenten (react-refresh).
 */
export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /**
   * Ein bis zwei ganze Sätze: wofür der Bereich da ist und was man dort tut.
   * Beschreibt den tatsächlichen Funktionsumfang — inklusive der Grenzen
   * („nur lesbar", „nicht änderbar"), damit niemand dort etwas sucht, was es
   * nicht gibt.
   */
  description: string;
  permission?: Permission;
}

// Permissions spiegeln die ProtectedRoute-Guards in App.tsx — ein Item ohne
// Zugriff zu zeigen führt den User nur in die „Kein Zugriff"-Seite (Audit
// 2026-07-02: Gast sah Mail-Inbox/KI-Vorschläge, beides 403).
export const HAUPT_ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: 'LayoutGrid',
    description:
      'Überblick über den aktuellen Stand: Kennzahlen zu aktiven Projekten, Fristen und offenen Vorschlägen, dazu Statusverteilung, Projektkarte und zuletzt bearbeitete Vorgänge. Jede Kachel springt in die Liste dahinter.',
  },
  {
    to: '/projects',
    label: 'Projekte',
    icon: 'FolderClosed',
    description:
      'Alle Brandschutznachweise mit Volltextsuche, Statusfiltern und Sortierung nach Frist oder Priorität. Über die Mehrfachauswahl lassen sich Projekte gebündelt hochstufen oder abschließen.',
  },
  {
    to: '/ai-proposals',
    label: 'KI-Vorschläge',
    icon: 'Sparkles',
    permission: Permission.AI_USE,
    description:
      'Sammelstelle für Feldvorschläge aus allen Projekten, gruppiert nach Projekt und filterbar nach Quelle und Mindest-Konfidenz. Hier übernehmen oder verwerfen Sie Vorschläge einzeln oder gebündelt.',
  },
  // Ansprechpartner haben bewusst KEINEN eigenen Menüpunkt: sie gehören immer
  // zu genau einem Kunden und werden in dessen Akte gepflegt. Die Gesamtliste
  // über alle Kunden (Suche, Massenimport) ist aus dem Kundenverzeichnis
  // heraus erreichbar — die Route /contacts bleibt gültig.
  {
    to: '/customers',
    label: 'Kunden',
    icon: 'Users',
    description:
      'Verzeichnis der Auftraggeber mit Suche und Absprung in die Kundenakte. Ansprechpartner werden in der jeweiligen Akte gepflegt; eine Gesamtliste über alle Kunden erreichen Sie von hier aus.',
  },
  {
    to: '/pruefer',
    label: 'Prüfsachverständige',
    icon: 'ShieldCheck',
    permission: Permission.PRUEFER_READ,
    description:
      'Stammdaten der Prüfsachverständigen und Prüfingenieure mit Fachrichtung, Bundesland und Prüfweg. Die Einträge werden in Projekten und Behördenvorgängen referenziert.',
  },
  // Ein Eingang statt zwei: Dokumente und E-Mails waren getrennte Menüpunkte,
  // obwohl beide dasselbe tun — es kommt etwas herein und muss einem Projekt
  // zugeordnet werden. Wer eine Mail mit Anhang bekam, musste raten, wo er
  // suchen soll. Die Route /mail bleibt für bestehende Verweise gültig.
  {
    to: '/documents',
    label: 'Eingang',
    icon: 'Inbox',
    description:
      'Alles, was hereinkommt: hochgeladene Dateien und eingegangene E-Mails. Upload per Drag-and-drop, Dateiauswahl oder Strg+V; die Zuordnung zum Projekt schlägt die KI vor. Ein zweiter Reiter zeigt alle bereits zugeordneten Dokumente.',
  },
];

// Verwaltungsblock: Stammdaten und Konfiguration statt Tagesgeschäft.
// „Vorlagen" gehört fachlich hierher (Nutzer-Feedback) — die Route braucht
// aber nur PROJECT_READ, deshalb steht der Eintrag ohne USER_MANAGE-Gate im
// Block. Ein Nicht-Admin sieht den Block also mit genau diesem einen Eintrag.
export const VERWALTUNG_ITEMS: NavItem[] = [
  {
    to: '/templates',
    label: 'Vorlagen',
    icon: 'LayoutTemplate',
    // Nur wer Projekte anlegen darf, hat von Vorlagen etwas. Ein Gast sah
    // hier bisher eine Liste, mit der er nichts anfangen kann.
    permission: Permission.PROJECT_CREATE,
    description:
      'Wiederverwendbare Projektvorlagen mit vorbelegten Feldern je Gebäudeart und Gebäudeklasse. Hier anlegen, bearbeiten und archivieren — aus einem bestehenden Projektstand entsteht eine Vorlage in der Projektansicht.',
  },
  {
    to: '/mangel-vorlagen',
    label: 'Textbibliothek',
    icon: 'BookOpen',
    // Dieselbe Linie wie bei den Projektvorlagen: Die Route ist lesbar für
    // jeden mit Mängel-Leserecht, der Menüpunkt zeigt sich aber nur dem, der
    // die Bibliothek auch pflegen kann — ein Gast hätte hier nichts zu tun.
    permission: Permission.MANGEL_VORLAGE_MANAGE,
    description:
      'Wiederkehrende Befunde mit Beschreibung, Maßnahme, Grundlage und Schweregrad. Die Erfassungsmaske eines Mangels schlägt sie aus Stichworten vor; hier anlegen, bearbeiten und deaktivieren.',
  },
  {
    to: '/admin/users',
    label: 'Benutzer',
    icon: 'UserCog',
    permission: Permission.USER_MANAGE,
    description:
      'Konten, Rollen und Aktiv-Status der Mitarbeitenden. Sie laden neue Benutzer ein, ändern Rollen und setzen Passwörter zurück — ein Reset beendet sofort alle offenen Sitzungen des Kontos.',
  },
  {
    to: '/admin/audit',
    label: 'Audit-Log',
    icon: 'History',
    permission: Permission.USER_MANAGE,
    description:
      'Protokoll aller Änderungen, filterbar nach Benutzer, Aktion, Entität und Zeitraum, mit aufklappbarem Änderungs-Diff. Die Einträge sind ausschließlich lesbar.',
  },
  {
    to: '/admin/rules',
    label: 'Prüfregeln',
    icon: 'ListChecks',
    permission: Permission.USER_MANAGE,
    description:
      'Schweregrad der fachlichen Prüfregeln festlegen (Fehler, Abweichung, Hinweis) und einzelne Regeln mit Begründung abschalten. Die hinterlegten Schwellenwerte der Bundesländer sind nur einsehbar, nicht änderbar.',
  },
  {
    to: '/admin/ai-usage',
    label: 'KI-Nutzung',
    icon: 'BarChart3',
    permission: Permission.USER_MANAGE,
    description:
      'Auswertung des KI-Verbrauchs über 7, 30 oder 90 Tage: Aufrufe, Token und geschätzte Kosten, aufgeschlüsselt nach Funktion und Nutzer. Eine reine Anzeige, hier wird nichts eingestellt.',
  },
  {
    to: '/admin/features',
    label: 'Feature-Flags',
    icon: 'ToggleRight',
    permission: Permission.USER_MANAGE,
    description:
      'Einzelne Funktionsbereiche der Anwendung an- und abschalten. Ein abgeschaltetes Feature verschwindet vollständig, samt Menüpunkten und zugehörigen Schnittstellen.',
  },
  {
    to: '/admin/feedback',
    label: 'Rückmeldungen',
    icon: 'MessageSquare',
    permission: Permission.FEEDBACK_MANAGE,
    description:
      'Fehler und Wünsche, die Nutzer aus der Anwendung heraus gemeldet haben, mit Übermittlungsstand. Ist eine GitHub-Anbindung eingerichtet, entsteht daraus je Meldung ein Issue; offene lassen sich hier nachreichen.',
  },
  {
    // Route bleibt `/preise`: bestehende Deep-Links und die Verweise in Hilfe
    // und Handbuch sollen weiter greifen. Umbenannt wurde nur die Beschriftung.
    to: '/preise',
    label: 'Nutzen & Preise',
    icon: 'Tag',
    permission: Permission.USER_MANAGE,
    description:
      'Nutzen und Preisgestaltung für das Angebotsgespräch: Wertargumentation, Inbetriebnahme-Pakete, Lizenzstaffeln, enthaltene KI-Nutzung und ein Rechenbeispiel. Eine reine Darstellung — hier wird nichts bestellt oder abgeschlossen.',
  },
  {
    to: '/admin/branding',
    label: 'Customizing',
    icon: 'PenTool',
    permission: Permission.USER_MANAGE,
    description:
      'Logo, Bürodaten und Footer-Texte der Anwendung sowie Farbe und Schrift für erzeugte Dokumente. Die Werte gelten global und lassen sich je Kunde im Kundendatensatz überschreiben.',
  },
];

/** Dauerhaft sichtbarer Fußbereich — steht bewusst außerhalb des Scrollbereichs. */
export const FUSS_ITEMS: NavItem[] = [
  {
    to: '/hilfe',
    label: 'Hilfe',
    icon: 'HelpCircle',
    description:
      'Handbuch mit Volltextsuche über alle Kapitel, dazu Schulungsplattform und Video-Bibliothek. Suchen ohne Treffer werden anonym gezählt, damit fehlende Kapitel nachgezogen werden.',
  },
  {
    to: '/settings',
    label: 'Einstellungen',
    icon: 'Settings2',
    description:
      'Persönliche Einstellungen: Erscheinungsbild, Anzeigemodus der Formularhilfen, Passwort und Zwei-Faktor-Anmeldung. Name und Rolle ändert nur die Benutzerverwaltung.',
  },
];

/** Alle Einträge in Anzeigereihenfolge — Grundlage der Eindeutigkeitsprüfung. */
export const ALLE_NAV_ITEMS: NavItem[] = [...HAUPT_ITEMS, ...VERWALTUNG_ITEMS, ...FUSS_ITEMS];
